import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Tag from 'shopper/components/Tag/V2';
import Text from 'shopper/components/Text';

import Image from 'components/Image';

import useCouponPicker from 'hooks/useCouponPicker';
import useExclusiveCoupon from 'hooks/useExclusiveCoupon';

import { sendEvent } from 'lib/gtag';
import { getStoreImageUrl } from 'lib/image';
import placeholder from 'lib/placeholder';
import { trackEvent } from 'lib/posthog';

import COUPONS from 'constants/coupons';

// dayjs() at render time causes SSR/client hydration mismatch — suppress SSR.
const VerificationBadge = dynamic(() => import('./VerificationBadge'), {
  ssr: false,
});

const getSimplifiedCouponDiscount = (couponDiscountShort) =>
  couponDiscountShort.split(' OFF')[0];

const CouponListItem = ({
  className,
  couponCode,
  couponDiscountShort,
  couponId,
  couponInstructions = null,
  couponTag = [],
  couponTitle,
  couponUntil = null,
  couponVerified = null,
  gaEventCategory,
  isCouponVisible = false,
  storeImage = null,
  storeName,
  storeSlug,
  visibility,
  withCouponStoreCover = false,
}) => {
  const {
    formatGaEventActionByVisibility,
    isCouponBlocked,
    onUnloggedUserTriesOpenExclusiveCoupon,
  } = useExclusiveCoupon();
  const { openCouponPicker } = useCouponPicker();
  const [isRulesOpen, setIsRulesOpen] = useState(false);

  const rulesId = `coupon-rules-${couponId}`;
  const rulesToggleId = `coupon-rules-toggle-${couponId}`;

  const tags = useMemo(
    () => couponTag.filter((tag) => !COUPONS.NOISE_TAGS.has(tag.trim())),
    [couponTag]
  );

  const handleClick = () => {
    sendEvent({
      action: formatGaEventActionByVisibility('coupon_click', visibility),
      category: gaEventCategory,
    });

    if (isCouponBlocked(visibility)) {
      onUnloggedUserTriesOpenExclusiveCoupon(visibility, () => {
        openCouponPicker({ couponId, storeName, storeSlug });
      });
      return;
    }

    trackEvent('click coupon card', {
      id: couponId,
      visibility: isCouponVisible ? 'visible' : 'hidden',
    });

    openCouponPicker({ couponId, storeName, storeSlug });
  };

  return (
    <div
      className={twMerge(
        'overflow-hidden rounded-4 border-2 border-neutral-high-200 dark:border-neutral-low-200',
        className
      )}
    >
      <Box className="grid min-h-36 grid-cols-[96px_1fr] gap-x-2 gap-y-2 p-4 md:grid-cols-[auto_1fr_1fr_1fr_200px] md:gap-x-8 md:px-8 md:py-6">
        <div className="row-span-2 flex h-24 w-24 flex-col items-center justify-center self-center border-r border-dashed border-neutral-high-400 pr-6 dark:border-neutral-low-200 md:pr-8 xl:w-40">
          {withCouponStoreCover ? (
            <Image
              alt={`Imagem da loja ${storeName}`}
              className="rounded-3"
              height={96}
              src={getStoreImageUrl(storeImage, 160)}
              width={96}
            />
          ) : (
            <>
              <Heading
                as="h3"
                className="cursor-pointer break-words text-center text-neutral-low-400 dark:text-neutral-high-200 lg:text-[28px]"
                size="size2"
                onClick={handleClick}
              >
                {getSimplifiedCouponDiscount(couponDiscountShort)}
              </Heading>
              <Text className="break-words text-center" size="size1">
                {placeholder('subtitles.coupon')}
              </Text>
              {couponInstructions && (
                <button
                  aria-controls={rulesId}
                  aria-expanded={isRulesOpen}
                  className="mt-1 inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-neutral-high-500 hover:text-neutral-low-400 dark:text-neutral-high-300 dark:hover:text-neutral-high-200"
                  id={rulesToggleId}
                  type="button"
                  onClick={() => setIsRulesOpen((prev) => !prev)}
                >
                  <Icon
                    className={twMerge(
                      'transition-transform duration-200',
                      isRulesOpen && 'rotate-90'
                    )}
                    name="chevron-right"
                    size="size4"
                  />
                  Regras
                </button>
              )}
            </>
          )}
        </div>
        <div className="col-start-2 row-span-2 flex flex-col gap-2 self-center md:contents">
          <div className="flex flex-col gap-2 md:col-span-3 md:col-start-2 md:row-span-2 md:self-center">
            <VerificationBadge
              couponUntil={couponUntil}
              couponVerified={couponVerified}
            />
            <Heading
              as="h2"
              className="cursor-pointer break-words text-sm font-semibold text-neutral-low-400 first-letter:uppercase dark:text-neutral-high-200 lg:text-xl"
              size="size4"
              onClick={handleClick}
            >
              {couponTitle}
            </Heading>
            {tags.length > 0 && (
              <div className="hidden flex-wrap gap-2 md:flex">
                {tags.map((tag) => (
                  <Tag
                    key={tag}
                    className="max-w-32 whitespace-normal md:max-w-fit md:whitespace-pre"
                    size="size3"
                    type="neutral"
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            )}
          </div>
          {/* Deeplink visitors (isCouponVisible) see the coupon code but still go
            through the full redirect flow (handleClick → openCouponPicker) to
            ensure affiliate session attribution via double-tab. */}
          {isCouponVisible &&
          visibility !== COUPONS.VISIBILITY_STATUS.APP_ONLY ? (
            <Button
              buttonTextClassName="text-sm md:text-base"
              className="col-start-2 h-8 w-full select-none self-center border-2 border-dashed border-neutral-high-400 min-size-8 dark:border-neutral-high-400 md:col-span-1 md:col-start-5 md:row-span-2 md:h-12 md:w-[200px] md:self-center md:justify-self-end md:min-size-10"
              size="size3"
              type="neutral-stroke"
              full
              onClick={handleClick}
            >
              {couponCode || COUPONS.COUPON_WITH_NO_CODE}
            </Button>
          ) : (
            <Button
              buttonTextClassName="text-sm md:text-base"
              className="group relative col-start-2 h-8 w-full self-center min-size-8 hover:border-primary-400 hover:bg-primary-400 md:col-span-1 md:col-start-5 md:row-span-2 md:h-12 md:w-[200px] md:self-center md:justify-self-end md:min-size-10"
              size="size3"
              type="primary"
              full
              onClick={handleClick}
            >
              <div className="absolute -right-px -top-px size-6 rounded-bl-[0.45rem] rounded-tr-[0.45rem] border-2 border-dashed border-neutral-high-400 bg-neutral-high-100 transition-all duration-200 ease-linear after:absolute after:-right-[2px] after:-top-[2px] after:size-0 after:rounded-bl-[7px] after:border-b-0 after:border-l-[24px] after:border-r-0 after:border-t-[24px] after:border-solid after:border-b-transparent after:border-l-neutral-low-500 after:border-r-transparent after:border-t-transparent after:transition-all after:duration-200 after:ease-linear after:content-empty group-hover:size-8 group-hover:after:border-l-[32px] group-hover:after:border-t-[32px] dark:bg-neutral-low-300" />
              {placeholder('actions.pickCoupon')}
            </Button>
          )}
        </div>
      </Box>
      {couponInstructions && !withCouponStoreCover && (
        <div
          aria-labelledby={rulesToggleId}
          className={twMerge(
            'overflow-hidden transition-all duration-200 ease-in-out',
            isRulesOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
          )}
          id={rulesId}
          role="region"
        >
          <div className="mx-4 border-t border-neutral-high-300 px-0 pb-4 pt-4 dark:border-neutral-low-200 md:mx-8">
            <Text
              className="text-neutral-high-500 first-letter:uppercase dark:text-neutral-high-300"
              size="size2"
            >
              {couponInstructions}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

CouponListItem.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  couponCode: PropTypes.string.isRequired,
  couponDiscountShort: PropTypes.string.isRequired,
  couponId: PropTypes.number.isRequired,
  couponInstructions: PropTypes.string,
  couponTag: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  couponTitle: PropTypes.string.isRequired,
  couponUntil: PropTypes.string,
  couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
  isCouponVisible: PropTypes.bool,
  storeImage: PropTypes.string.isRequired,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
  visibility: PropTypes.string.isRequired,
  withCouponStoreCover: PropTypes.bool,
};

export default CouponListItem;
